import React, { Component } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { scaleOrdinal } from "d3-scale";
import cx from "classnames";

import TaxonomyLevelSelect from "../TaxonomyLevelSelect";
import TaxonomySummaryTable from "../TaxonomySummaryTable";
import OptimizedSelect from "../OptimizedSelect";
import * as SortingControls from "./SortingControls/SortingControls.bs";
import BarChartArea from "./BarChartArea";

import { categorical } from "lib/colors";

import styles from "./style.css";

class BarChart extends Component {
  static propTypes = {
    chartControls: PropTypes.shape({
      taxonomyLevel: PropTypes.number.isRequired,
      barExpand: PropTypes.bool.isRequired,
      barSortingCriteria: PropTypes.array.isRequired
    }).isRequired,
    setChartControls: PropTypes.func.isRequired
  };

  state = {
    selectedTaxon: null,
    hiddenTaxonomies: []
  };

  handleExpandChange = barExpand => () => {
    this.props.setChartControls({ barExpand });
  };

  handleTaxonomyLevelChange = taxonomyLevel => {
    this.setState({ hiddenTaxonomies: [], selectedTaxon: null });
    this.props.setChartControls({ taxonomyLevel });
  };

  handleSelectedTaxonSelectChange = option => {
    const selectedTaxon = option ? option.value : null;
    this.handleSelectedTaxonChange(selectedTaxon);
  };

  handleSelectedTaxonChange = selectedTaxon => {
    this.setState({ selectedTaxon });
  };

  handleHiddenTaxonomiesChange = hiddenTaxonomies => {
    this.setState({ hiddenTaxonomies });
  };

  render() {
    const { getDataForChart, navigateToSample, chartControls } = this.props;
    const {
      taxonomyLevel,
      barExpand: expand,
      barSortingCriteria: sortingCriteria
    } = chartControls;

    const { selectedTaxon, hiddenTaxonomies } = this.state;

    const {
      chartData,
      sampleIds,
      sortingAttributes,
      taxonomies,
      getTaxonomyDistributionInDataset,
      getTaxonomyDistributionInSample,
      getTaxonomiesTotalCounts
    } = getDataForChart(taxonomyLevel, sortingCriteria);

    const visibleTaxonomies = taxonomies.filter(
      taxonomy => hiddenTaxonomies.indexOf(taxonomy) === -1
    );

    const getTaxonomyColor = scaleOrdinal()
      .domain(taxonomies)
      .range(categorical);

    const formattedCounts = (
      <strong>
        <FormattedNumber value={getTaxonomiesTotalCounts(visibleTaxonomies)} />
      </strong>
    );
    const formattedTaxonomies =
      visibleTaxonomies.length === taxonomies.length ? (
        <strong>
          <FormattedNumber value={taxonomies.length} />
        </strong>
      ) : (
        <strong>
          <FormattedNumber value={visibleTaxonomies.length} />
          {" / "}
          <FormattedNumber value={taxonomies.length} />
        </strong>
      );
    const formattedSamples = (
      <strong>
        <FormattedNumber value={sampleIds.length} />
      </strong>
    );

    return (
      <div>
        <div className={styles.controls}>
          <TaxonomyLevelSelect
            value={taxonomyLevel}
            onChange={this.handleTaxonomyLevelChange}
          />
          <div className={styles.expandControls}>
            <button
              onClick={this.handleExpandChange(false)}
              className={cx({ [styles.active]: !expand })}
            >
              <FormattedMessage id="biom.taxonomicAnalysis.barChart.expandOptions.counts" />
            </button>
            <button
              onClick={this.handleExpandChange(true)}
              className={cx({ [styles.active]: expand })}
            >
              <FormattedMessage id="biom.taxonomicAnalysis.barChart.expandOptions.percentages" />
            </button>
          </div>
        </div>
        <div className={styles.controls}>
          <p className={styles.summary}>
            <FormattedMessage
              id="biom.taxonomicAnalysis.barChart.summary"
              values={{
                samples: formattedSamples,
                taxonomies: formattedTaxonomies,
                counts: formattedCounts
              }}
            />
          </p>
          <OptimizedSelect
            value={selectedTaxon}
            onChange={this.handleSelectedTaxonSelectChange}
            values={taxonomies}
            clearable
            placeholder={
              <FormattedMessage id="biom.taxonomicAnalysis.barChart.search.placeholder" />
            }
          />
        </div>
        <div className={styles.sortingControls}>
          <SortingControls.make attributes={sortingAttributes} />
        </div>
        <div className={styles.chart}>
          <BarChartArea
            key={`${taxonomyLevel}-${hiddenTaxonomies.join("-")}`}
            data={chartData}
            selectedTaxon={selectedTaxon}
            sampleIds={sampleIds}
            taxonomies={visibleTaxonomies}
            expand={expand}
            getTaxonomyDistributionInDataset={getTaxonomyDistributionInDataset}
            getTaxonomyDistributionInSample={getTaxonomyDistributionInSample}
            getTaxonomyColor={getTaxonomyColor}
            onSelectedTaxonChange={this.handleSelectedTaxonChange}
            navigateToSample={navigateToSample}
          />
        </div>
        <div className={styles.item}>
          <TaxonomySummaryTable
            taxonomies={taxonomies}
            sampleIds={sampleIds}
            getTaxonomyDistributionInDataset={getTaxonomyDistributionInDataset}
            getTaxonomyDistributionInSample={getTaxonomyDistributionInSample}
            getTaxonomyColor={getTaxonomyColor}
            hiddenTaxonomies={hiddenTaxonomies}
            onChangeHiddenTaxonomies={this.handleHiddenTaxonomiesChange}
            selectedTaxonomy={selectedTaxon}
          />
        </div>
      </div>
    );
  }
}

export default BarChart;
